import { createFileRoute } from "@tanstack/react-router";
import { SiteLayout } from "@/components/site/SiteLayout";
import {
  ArrowRight,
  ArrowDown,
  ArrowLeft,
  Package,
  Box,
  File,
  FileText,
  Ticket,
} from "lucide-react";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/private-label")({
  head: () => ({
    meta: [
      ...seo({
        title: "OEM / Private Label Solutions – QT Foods Wholesale",
        description:
          "Tailored ingredient proposals, tea processing, and packaging solutions backed by extensive OEM experience.",
      }),
    ],
  }),
  component: Page,
});

const FLOW_STEPS = [
  {
    num: 1,
    title: "Inquiry",
    desc: "Please contact us via the inquiry form on our website.",
  },
  {
    num: 2,
    title: "Sample Selection & Tasting",
    desc: "We recommend starting with sample kits to evaluate quality, taste, and product fit for your market before product development.",
  },
  {
    num: 3,
    title: "Consultation",
    desc: "Based on your feedback, we discuss your requirements, target market, and product direction to move your project forward.",
  },
  {
    num: 4,
    title: "Product Proposal",
    desc: "We propose the most suitable tea products and packaging formats based on your target market and application.",
  },
  {
    num: 5,
    title: "Initial Quotation",
    desc: "Based on the proposed specifications, we provide an initial quotation to help you understand the cost structure before moving forward.",
  },
  {
    num: 6,
    title: "Sampling & Prototyping",
    desc: "We prepare samples and prototypes, refining specifications while confirming taste, aroma, and performance for your intended application.",
  },
  {
    num: 7,
    title: "Final Quotation",
    desc: "After finalizing all specifications, we provide a confirmed quotation.",
  },
  {
    num: 8,
    title: "Order Confirmation",
    desc: "Once you approve the quotation and specifications, you place a formal order.",
  },
  {
    num: 9,
    title: "Production & Processing",
    desc: "Production is carried out under strict quality control based on the confirmed specifications.",
  },
  {
    num: 10,
    title: "Shipment & Delivery",
    desc: "Products are carefully inspected prior to shipment and delivered according to the agreed schedule.",
  },
];

function Page() {
  return (
    <SiteLayout breadcrumbs={[{ label: "OEM / Private Label Solutions" }]}>
      <section className="mx-auto max-w-5xl px-5 pt-12 pb-24 lg:px-8 lg:pt-16">
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">
          OEM / Private Label Solutions
        </h1>

        <div className="mt-8">
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl">
            For companies considering original products using Matcha or Japanese tea, we provide
            tailored ingredient proposals, tea processing, and packaging solutions backed by
            extensive OEM experience.
          </p>
        </div>

        <div className="mt-20">
          <h2 className="text-3xl font-serif mb-10">Package Types</h2>
          <div className="flex flex-wrap gap-10 justify-start items-end">
            <div className="flex flex-col items-center gap-4 w-28 text-center">
              <img
                src="/assets/images/packaging/pkg-tin.svg"
                alt="Tin Packaging"
                width={100}
                height={100}
                className="h-24 w-auto object-contain drop-shadow-sm"
              />
              <span className="text-sm font-medium">Tin</span>
            </div>
            <div className="flex flex-col items-center gap-4 w-28 text-center">
              <img
                src="/assets/images/packaging/pkg-flat-pouch.svg"
                alt="Flat Pouch Packaging"
                width={100}
                height={100}
                className="h-24 w-auto object-contain drop-shadow-sm"
              />
              <span className="text-sm font-medium">Flat Pouch</span>
            </div>
            <div className="flex flex-col items-center gap-4 w-28 text-center">
              <img
                src="/assets/images/packaging/pkg-gusset-pouch.svg"
                alt="Gusset Pouch Packaging"
                width={100}
                height={100}
                className="h-24 w-auto object-contain drop-shadow-sm"
              />
              <span className="text-sm font-medium">Gusset Pouch</span>
            </div>
            <div className="flex flex-col items-center gap-4 w-28 text-center">
              <img
                src="/assets/images/packaging/pkg-standup-pouch.svg"
                alt="Stand Up Pouch Packaging"
                width={100}
                height={100}
                className="h-24 w-auto object-contain drop-shadow-sm"
              />
              <span className="text-sm font-medium">Stand Up Pouch</span>
            </div>
            <div className="flex flex-col items-center gap-4 w-28 text-center">
              <img
                src="/assets/images/packaging/pkg-sachet.svg"
                alt="Sachet Packaging"
                width={100}
                height={100}
                className="h-24 w-auto object-contain drop-shadow-sm"
              />
              <span className="text-sm font-medium">
                Sachet
                <br />
                <span className="text-xs text-muted-foreground">(Single serve)</span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-24">
          <h2 className="text-3xl font-serif mb-10">Sample Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="overflow-hidden rounded-md border border-border/50 bg-white">
              <img
                src="/assets/images/private-label/sample-2.png"
                alt="Private label Matcha in a gold tin"
                width={500}
                height={500}
                className="w-full h-auto object-cover aspect-square"
              />
            </div>
            <div className="overflow-hidden rounded-md border border-border/50 bg-white">
              <img
                src="/assets/images/private-label/sample-3.png"
                alt="Private label Superior Ceremonial Matcha in a gold tin"
                width={500}
                height={500}
                className="w-full h-auto object-cover aspect-square"
              />
            </div>
            <div className="overflow-hidden rounded-md border border-border/50 bg-white">
              <img
                src="/assets/images/private-label/sample-4.png"
                alt="Private label Sencha in a white stand-up pouch"
                width={500}
                height={500}
                className="w-full h-auto object-cover aspect-square object-center"
              />
            </div>
            <div className="overflow-hidden rounded-md border border-border/50 bg-white">
              <img
                src="/assets/images/private-label/sample-5.png"
                alt="Private label Lemon Sencha in a white stand-up pouch"
                width={500}
                height={500}
                className="w-full h-auto object-cover aspect-square object-center"
              />
            </div>
          </div>
        </div>

        <div className="mt-24">
          <div className="mb-12">
            <h2 className="text-3xl font-serif uppercase tracking-wider mb-2">FLOW</h2>
            <p className="text-xl font-medium">From Inquiry to Delivery</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FLOW_STEPS.map((step, i) => (
              <div key={i} className="bg-muted/40 p-6 rounded-md border border-border/50">
                <h3 className="font-bold mb-3 text-lg tracking-tight">
                  {step.num}. {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
